/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import z from 'zod';
import { bidProducts, DEFAULT_PROJECT_OBJECT, projectObjectSchema, } from '../salesforce';
import { formatDate, objectKeys } from '../utils';
import { compoundVersionIdSchemaZ } from './components';
import { probabilityOfExceedances, } from './ess-costing';
import { BidRequestProjectTeam, bidRequestInputTeamApprovals, } from './project-team-roles';
export var OpportunityStage;
(function (OpportunityStage) {
    OpportunityStage["QUALIFICATION"] = "Qualification";
    OpportunityStage["SCOPING"] = "Scoping";
    OpportunityStage["SUBMITTED"] = "Submitted";
    OpportunityStage["SHORTLISTED"] = "Shortlisted";
    OpportunityStage["TERM_SHEET_EXEC"] = "Term Sheet Executed";
    OpportunityStage["NEGOTIATION"] = "Negotiation";
    OpportunityStage["CONTRACT_EXEC"] = "Contract Execution";
    OpportunityStage["CONTRACT_AWARDED"] = "Contract Awarded";
    OpportunityStage["NEGOTIATE_LOSS"] = "Negotiated but lost";
    OpportunityStage["DID_NOT_PARTICIPATE"] = "Did Not Participate";
    OpportunityStage["PASS"] = "Pass";
})(OpportunityStage || (OpportunityStage = {}));
export var BidRequestInputApprovalStatus;
(function (BidRequestInputApprovalStatus) {
    BidRequestInputApprovalStatus["NOT_REVIEWED"] = "Not Reviewed";
    BidRequestInputApprovalStatus["APPROVED"] = "Approved";
    BidRequestInputApprovalStatus["CHANGES_REQUESTED"] = "Changes Requested";
})(BidRequestInputApprovalStatus || (BidRequestInputApprovalStatus = {}));
export var BidRequestStatus;
(function (BidRequestStatus) {
    // The parallel dev/txm/eng/procurement workflows run concurrently, so no
    // single stored status can honestly name "the" bottleneck. While any of them
    // is outstanding the bid request is IN_REVIEW; the per-workflow approval
    // columns (and the badges rendered from them) carry which workflows are
    // pending (PA-4188). The values below IN_REVIEW are genuinely single-valued:
    // finance approval and pricing/completion are sequential, reached only after
    // the parallel phase; changes/completed/aborted are terminal-ish lifecycle.
    BidRequestStatus["IN_REVIEW"] = "In Review";
    BidRequestStatus["AWAITING_FINANCE_APPROVAL"] = "Awaiting Finance approval";
    BidRequestStatus["AWAITING_PRICING"] = "Awaiting pricing";
    BidRequestStatus["AWAITING_COMPLETION"] = "Awaiting completion";
    BidRequestStatus["CHANGES_REQUESTED"] = "Changes requested";
    BidRequestStatus["COMPLETED"] = "Completed";
    BidRequestStatus["ABORTED"] = "Aborted";
})(BidRequestStatus || (BidRequestStatus = {}));
// Per-workflow "outstanding" filter selectors. These are NOT stored statuses —
// the engine stores IN_REVIEW during the parallel phase — they exist only as
// list-filter tokens that the backend maps to `<workflow>_approval <> 'Approved'`
// (see generateStatusFilters). Kept separate from BidRequestStatus so the stored
// status enum stays honest.
export var BidRequestWorkflowFilter;
(function (BidRequestWorkflowFilter) {
    BidRequestWorkflowFilter["AWAITING_DEV"] = "Awaiting Development";
    BidRequestWorkflowFilter["AWAITING_TXM"] = "Awaiting Transmission";
    BidRequestWorkflowFilter["AWAITING_ENG"] = "Awaiting Engineering";
    BidRequestWorkflowFilter["AWAITING_PROCUREMENT"] = "Awaiting Procurement";
    BidRequestWorkflowFilter["AWAITING_FINANCE"] = "Awaiting Finance";
})(BidRequestWorkflowFilter || (BidRequestWorkflowFilter = {}));
export var NetworkUpgradeTiming;
(function (NetworkUpgradeTiming) {
    NetworkUpgradeTiming["AT_COD"] = "At COD";
    NetworkUpgradeTiming["PRORATA_DURING_CONSTRUCTION"] = "Pro-rata during Construction";
    NetworkUpgradeTiming["OTHER"] = "Other";
})(NetworkUpgradeTiming || (NetworkUpgradeTiming = {}));
export const deliveryPoints = ['Bus Bar', 'Hub settled'];
export const wtsfOptions = ['Min LCOE', 'Min LCOS', 'Min LCOx'];
export var AuxPower;
(function (AuxPower) {
    AuxPower["RETAIL"] = "Retail";
    AuxPower["TARIFF_STRUCTURE"] = "Tariff Structure";
})(AuxPower || (AuxPower = {}));
export const opportunityTransactions = ['PPA', 'BTA'];
export const essAvailabilityGuaranteeStructures = [
    'monthly',
    'seasonal',
    'annual',
    'not_required',
];
export var ScenarioApprovalStatus;
(function (ScenarioApprovalStatus) {
    ScenarioApprovalStatus["NOT_REVIEWED"] = "NOT_REVIEWED";
    ScenarioApprovalStatus["APPROVED"] = "APPROVED";
    ScenarioApprovalStatus["DECLINED"] = "DECLINED";
    ScenarioApprovalStatus["COMMENTED"] = "COMMENTED";
})(ScenarioApprovalStatus || (ScenarioApprovalStatus = {}));
export var BidRequestStage;
(function (BidRequestStage) {
    BidRequestStage["DRAFT"] = "Draft";
    BidRequestStage["READY_FOR_SUBMITTAL"] = "Ready for Submittal";
})(BidRequestStage || (BidRequestStage = {}));
const financeBidMatrixInputsSchema = z.strictObject({
    products: z.array(z.enum(bidProducts)),
    delivery_point: z.enum(deliveryPoints),
    contracted_term: z.number().nullable(),
    delivery_point_hub: z.string().nullable(),
    wtsf: z.union([z.enum(wtsfOptions), z.string()]),
    pv_ppa_price: z.number().nullable(),
    pv_ppa_escalation: z.number().nullable(),
    ess_capacity_payment: z.number().nullable(),
    ess_capacity_payment_esc: z.number().nullable(),
    wind_ppa_price: z.number().nullable(),
    applicable_icrs: z.array(z.string()), // bnCRM ids
    notes: z.string().nullable(),
});
const scenarioIdSchema = z.strictObject({
    id: z.number(),
    index: z.int().min(0).optional(),
});
export const bidMatrixScenarioSchema = z.strictObject({
    ...financeBidMatrixInputsSchema.shape,
    ...scenarioIdSchema.shape,
    project_id: z.string(), // bnCRM id
    project_object: projectObjectSchema.default(DEFAULT_PROJECT_OBJECT),
    plant_type: z.custom(),
    cod: z.string(),
    poi_mw_ac: z.number(),
    pv_ac_capacity: z.number().nullable(),
    ess_capacity: z.number().nullable(),
    ess_power: z.number().nullable(),
    ess_term: z.number().nullable(),
    ess_cycles: z.number().nullable(),
    ess_maintain_capacity: z.boolean(),
    ess_availability_guarantee_structure: z.enum(essAvailabilityGuaranteeStructures).optional(),
    ess_availability_guarantee_perc: z.number().optional(),
    ess_specific_offtake_power_factor: z.number().optional(),
    wind_capacity: z.number().nullable(),
});
export const bidMatrixScenarioOptionalIdSchema = bidMatrixScenarioSchema
    .omit({ id: true })
    .extend({ id: z.number().nullable().optional() });
// Exhaustive by design: adding a BidMatrixScenario field forces a notify-role decision here.
// Roles are candidates; engineering roles are plant-type filtered at call time.
export const bidMatrixFieldNotifyRoles = {
    id: [],
    index: [],
    project_id: [
        'dev_lead',
        'txm_planner',
        'procurement_lead',
        'dev_engineer',
        'bess_lead',
        'finance_lead',
    ],
    // No roles of its own: the object only changes when `project_id` does, and that entry already
    // notifies everyone who cares about a project swap.
    project_object: [],
    plant_type: ['procurement_lead', 'dev_engineer', 'bess_lead', 'finance_lead'],
    cod: ['procurement_lead', 'dev_engineer', 'bess_lead', 'finance_lead'],
    poi_mw_ac: ['txm_planner', 'dev_engineer', 'bess_lead', 'finance_lead'],
    pv_ac_capacity: ['dev_engineer', 'finance_lead'],
    wind_capacity: ['finance_lead'],
    ess_capacity: ['bess_lead', 'finance_lead'],
    ess_power: ['bess_lead', 'finance_lead'],
    ess_term: ['bess_lead', 'finance_lead'],
    ess_cycles: ['bess_lead', 'finance_lead'],
    ess_maintain_capacity: ['bess_lead', 'finance_lead'],
    ess_availability_guarantee_structure: ['bess_lead', 'finance_lead'],
    ess_availability_guarantee_perc: ['bess_lead', 'finance_lead'],
    ess_specific_offtake_power_factor: ['bess_lead', 'finance_lead'],
    products: ['finance_lead'],
    delivery_point: ['finance_lead'],
    delivery_point_hub: ['finance_lead'],
    contracted_term: ['finance_lead'],
    wtsf: ['finance_lead'],
    pv_ppa_price: ['finance_lead'],
    pv_ppa_escalation: ['finance_lead'],
    ess_capacity_payment: ['finance_lead'],
    ess_capacity_payment_esc: ['finance_lead'],
    wind_ppa_price: ['finance_lead'],
    applicable_icrs: ['txm_planner', 'finance_lead'],
    notes: [],
};
export const selectedSitesSchema = z.strictObject({
    lease: z.array(z.string()),
    purchase: z.array(z.string()),
    owned_in_fee: z.array(z.string()),
});
export const coreLandSpecificDataSchema = z.strictObject({
    lease_cost: z.number().nullable(),
    lease_escalation: z.number().nullable(),
    purchase_cost: z.number().nullable(),
    lease_acres_under_control: z.number().nullable(),
    purchase_acres_under_control: z.number().nullable(),
    owned_in_fee_acres: z.number().nullable().optional(),
    owned_in_fee_cost: z.number().nullable().optional(),
});
export const projectLandSpecificDataSchema = coreLandSpecificDataSchema
    .extend({
    coordinates: z.tuple([z.number(), z.number()]),
    miles_to_ic: z.number().nullable(),
    min_lease_acres: z.number().nullable(),
    min_purchase_acres: z.number().nullable(),
    total_easement_costs: z.number().nullable().optional(),
    site_perc_timber: z.number().nullable(),
    comment: z.string().nullable(),
    selected_sites: selectedSitesSchema.optional(),
})
    .strict();
export const discretionaryCostsSchema = z.strictObject({
    name: z.string(),
    date: z.string(),
    value: z.number(),
});
export const projectDevSpecificDataSchema = z.strictObject({
    devex_budget: z.number().nullable(),
    energy_communities_bonus: z.boolean(),
    budget_comment: z.string().nullable().optional(),
    discretionary_costs: z.array(discretionaryCostsSchema).optional(),
});
export const icrSpecificDataSchema = z.strictObject({
    // M&A projects have no bnCRM ICR records, so users add ICRs on the fly. Their identity lives
    // here rather than in Salesforce; `ad_hoc` marks the ones with no bnCRM record behind them.
    name: z.string().nullable().optional(),
    ad_hoc: z.boolean().optional(),
    reimb_nu_costs: z.number().nullable(),
    nonreimb_nu_costs: z.number().nullable(),
    refund_period_months: z.number().nullable(),
    refund_interest_rate: z.number().nullable(),
    lc_posting_date: z.string().nullable(),
    lc_posting_cost: z.number().nullable(),
    ic_voltage_kv: z.number().nullable(),
    poi_mw_ac: z.number(),
    nu_payment_timing: z.enum(NetworkUpgradeTiming),
    nu_payment_timing_other_comments: z.string().nullable(),
    aux_power: z.enum(AuxPower),
    aux_power_comment: z.string().nullable(),
});
export const projectTxmSpecificDataSchema = z.strictObject({
    icrs: z.record(z.string(), icrSpecificDataSchema),
    merchant_prices_id: z.string(),
    curtailment: z.number().nullable(),
    contract_period_wheeling_first_year: z.number().nullable(),
    merchant_period_wheeling_first_year: z.number().nullable(),
    comment: z.string().nullable(),
});
export const projectSpecificDataSchema = z.strictObject({
    land: projectLandSpecificDataSchema.nullable().optional(),
    txm: projectTxmSpecificDataSchema.nullable().optional(),
    dev: projectDevSpecificDataSchema.nullable().optional(),
});
export const bidRequestTransactionDataSchema = z.strictObject({
    transaction: z.enum(['PPA', 'BTA']),
    ppa_security_pre_cod: z.number().nullable(),
    ppa_security_post_cod: z.number().nullable(),
    offtake_bid_fee: z.number().nullable(),
    offtake_success_broker_fee: z.number().nullable(),
    ppa_security_pre_cod_start_date: z.string().nullable(),
    probability_of_exceedance: z.enum(probabilityOfExceedances).nullable(),
});
export const baseNewBidRequestSchema = z.strictObject({
    name: z.string(),
    opportunity_id: z.string(),
    stage: z.enum(BidRequestStage),
    version: z.number(),
    comment: z.string(),
    transaction_data: bidRequestTransactionDataSchema,
});
export const bidRequestNotificationsPayloadSchema = z.strictObject({
    notifAudiencePerProject: z.record(z.string(), z.array(z.enum(objectKeys(BidRequestProjectTeam)))),
    others: z.array(z.string()).nullable(),
    comment: z.string().nullable(),
});
export const configIdsPerScenarioSchema = z.strictObject({
    bid_matrix_id: z.number(),
    pv: z.union([z.string(), compoundVersionIdSchemaZ]).nullable(),
    ess: z.union([z.string(), compoundVersionIdSchemaZ]).nullable(),
    fimo: z.union([z.string(), compoundVersionIdSchemaZ]).nullable(),
    comments: z.string().nullable(),
});
export const MAX_PROCUREMENT_MPTS = 5;
export const procurementMptSchema = z.strictObject({
    construction_start_date: z.iso.date().nullish(),
    cost_per_count: z.number().nullable(),
    mva_capacity: z.number().nullable(),
});
export const bidRequestProcurementInputsSchema = z.strictObject({
    pv_modules_cost_per_kw_dc: z.number().nullable(),
    mpts: z.array(procurementMptSchema).max(MAX_PROCUREMENT_MPTS),
    hv_breakers_cost_per_count: z.number().nullable(),
    other_equipment_notes: z.string().nullable(),
});
export const procurementInputsPerCodSchema = z.strictObject({
    project_id: z.string(),
    cod: z.coerce.date(),
    data: bidRequestProcurementInputsSchema,
});
export const editProcurementInputsPayloadSchema = z.strictObject({
    procurement_inputs: z.array(procurementInputsPerCodSchema),
});
export const editConfigsPayloadSchema = z.strictObject({
    configs: z.array(configIdsPerScenarioSchema),
});
export const newBidRequestPayloadSchema = baseNewBidRequestSchema.extend({
    bid_matrix: z.array(bidMatrixScenarioOptionalIdSchema),
    plant_types: z.array(z.custom()),
    notifications: bidRequestNotificationsPayloadSchema.optional(),
    deleted_scenarios: z.array(z.number()).optional(),
});
export const selectedSitesPerScenarioSchema = z.union([
    selectedSitesSchema.extend({ custom: z.literal(false).optional() }).strict(),
    z.strictObject({
        custom: z.literal(true),
        lease_cost: z.number(),
        lease_escalation: z.number(),
        purchase_cost: z.number(),
        lease_acres_under_control: z.number(),
        purchase_acres_under_control: z.number(),
        owned_in_fee_acres: z.number(),
        owned_in_fee_cost: z.number(),
    }),
]);
export const baseEditBidRequestInputsPayloadSchema = z.strictObject({
    plant_types: z.array(z.string()).optional(),
    project_update: z.record(z.string(), projectSpecificDataSchema).optional(),
    bid_matrix: z.array(bidMatrixScenarioOptionalIdSchema).optional(),
    transaction_data: bidRequestTransactionDataSchema.optional(),
    deleted_scenarios: z.array(z.number()).optional(),
    customized_sites: z.record(z.string(), selectedSitesPerScenarioSchema).optional(),
    comment: z.string().optional(),
});
export const editBidRequestInputsPayloadSchema = baseEditBidRequestInputsPayloadSchema.extend({
    notifications: bidRequestNotificationsPayloadSchema.optional(),
});
export const bidRequestQueryOptionsSchema = z.strictObject({
    id: z.string().nullable().optional(),
    opportunityId: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    projectIds: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    onlyMe: z.boolean().optional(),
    completionStatus: z.enum(['all', 'open', 'aborted', 'completed']).optional(),
    latestVersionOnly: z.boolean().optional(),
    staleOnly: z.boolean().optional(),
    limit: z.number().int().min(0).optional(),
    offset: z.number().int().min(0).optional(),
});
export const approvalLabelMap = {
    dev_approval: 'Development',
    eng_approval: 'Engineering',
    finance_approval: 'Finance',
    procurement_approval: 'Procurement',
    txm_approval: 'Transmission',
};
// TODO: find new home for `const`s
export const currentOpportunityStages = [
    OpportunityStage.QUALIFICATION,
    OpportunityStage.SCOPING,
    OpportunityStage.SUBMITTED,
    OpportunityStage.SHORTLISTED,
    OpportunityStage.TERM_SHEET_EXEC,
    OpportunityStage.NEGOTIATION,
    OpportunityStage.CONTRACT_EXEC,
    OpportunityStage.CONTRACT_AWARDED,
];
export const formatFimoInputsFileName = (version, devOrEng = 'eng') => `${formatDate(undefined, 'YYYY-MM-DD HH.mm')}_${_.upperFirst(devOrEng)}_FIMO_Input_v${version}`;
export const financeBidMatrixScenarioKeys = [
    'applicable_icrs',
    'products',
    'delivery_point',
    'contracted_term',
    'delivery_point_hub',
    'wtsf',
    'pv_ppa_price',
    'pv_ppa_escalation',
    'ess_capacity_payment',
    'ess_capacity_payment_esc',
    'wind_ppa_price',
    'notes',
];
export const bidRequestInputApprovalsPayloadSchema = bidRequestNotificationsPayloadSchema
    .omit({ comment: true })
    .extend({
    status: z.enum(BidRequestInputApprovalStatus),
    peLevel: z.enum(probabilityOfExceedances),
    comments: z.string().nullable(),
    approvals: z.array(z.enum(bidRequestInputTeamApprovals)),
    approveDisabled: z.string().optional(),
});
export const generatePlantType = (scenario) => {
    const plantTypes = [];
    if (scenario.pv_ac_capacity && Number(scenario.pv_ac_capacity) > 0) {
        plantTypes.push('PV');
    }
    if (scenario.wind_capacity && Number(scenario.wind_capacity) > 0) {
        plantTypes.push('Wind');
    }
    if (scenario.ess_capacity && Number(scenario.ess_capacity) > 0) {
        plantTypes.push('BESS');
    }
    return plantTypes.join('+');
};
//# sourceMappingURL=bid-requests.js.map